import React, { useEffect, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { getPartner } from 'common/misc'
import colors from 'common/colors'
import { adsFetched, adsError, adsLoading } from 'actions/ads'
import { fetchAds } from 'api/ads'
import type { AppState } from 'state/index'

const AD_ROTATION_INTERVAL_MS = 10000

export default function AdBanner(): React.ReactElement | null {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const hasFetchedRef = useRef(false)

  // Redux state
  const ads = useSelector((state: AppState) => state.ads.collection)
  const adsStatus = useSelector((state: AppState) => state.ads.status)
  const hardware = useSelector((state: AppState) => state.hardware.collection)
  const currentSubscription = useSelector(
    (state: AppState) => state.account.currentSubscription,
  )
  const tenant = useSelector((state: AppState) => state.account.singleton)
  const partnerContactDetails = useSelector(
    (state: AppState) => state.account.partnerContactDetails,
  )
  const partner = getPartner()

  // Local state
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showContactModal, setShowContactModal] = useState(false)
  const [partnerContact, setPartnerContact] = useState<any>(null)

  // Determine if ads should be displayed
  const isActivePremium =
    currentSubscription &&
    ['paid', 'comped', 'free_year'].includes(
      currentSubscription.subscriptionStatus,
    )
  const adsDisabled = isActivePremium && tenant?.prefs?.ads?.disabled === true

  // Filter ads based on hardware ownership
  const filteredAds = React.useMemo(() => {
    // If hardware not loaded yet, show all ads (unknown state)
    if (hardware.length === 0) {
      return ads
    }

    const result = ads.filter((ad) => {
      // Ads with no deviceType are always shown (general product ads)
      if (!ad.deviceType) {
        return true
      }

      // Find if tenant owns this device type
      const ownedHardwareWithType = hardware.filter(
        (hw) => hw.deviceType === ad.deviceType,
      )

      if (ownedHardwareWithType.length === 0) {
        // Tenant doesn't own this device type - only show if targetNonOwners is true
        return ad.targetNonOwners
      }
      return false
    })

    return result
  }, [ads, hardware])

  // Fetch ads on mount
  useEffect(() => {
    if (hasFetchedRef.current) return

    hasFetchedRef.current = true
    if (ads.length === 0 && adsStatus === 'idle') {
      dispatch(adsLoading() as any)
      fetchAds()
        .then((res) => {
          dispatch(adsFetched(res.data) as any)
        })
        .catch(() => {
          dispatch(adsError() as any)
        })
    }
  }, [])

  // Reset currentIndex when filteredAds length changes
  useEffect(() => {
    if (currentIndex >= filteredAds.length) {
      setCurrentIndex(0)
    }
  }, [filteredAds.length])

  // Auto-rotate timer
  useEffect(() => {
    if (filteredAds.length <= 1 || adsDisabled) return

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % filteredAds.length)
    }, AD_ROTATION_INTERVAL_MS)

    return () => clearInterval(timer)
  }, [filteredAds.length, adsDisabled])

  // Hide if no ads or ads disabled
  if (filteredAds.length === 0 || adsDisabled || adsStatus === 'error') {
    return null
  }

  const currentAd = filteredAds[currentIndex]
  if (!currentAd) return null

  const adImageUrl = `${AD_ASSETS_BASE_URL}/${currentAd.bannerImageUrl}`

  // Build alt text from ad data
  const altText = [
    currentAd.productName,
    currentAd.productVersion ? `v${currentAd.productVersion}` : '',
    Array.isArray(currentAd.productBenefits)
      ? currentAd.productBenefits.join('; ')
      : '',
  ]
    .filter(Boolean)
    .join(' - ')

  // Handle keyboard activation
  const handleAdKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleAdClick()
    }
  }

  // Handle ad click
  const handleAdClick = () => {
    if (partner !== 'farmtrx') {
      // Partner customer - show contact modal
      setPartnerContact(partnerContactDetails)
      setShowContactModal(true)
    } else {
      // Direct customer - open store URL
      if (currentAd.storeUrl) {
        window.open(currentAd.storeUrl, '_blank', 'noopener')
      }
    }
  }

  // Navigation handlers
  const handlePrev = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + filteredAds.length) % filteredAds.length,
    )
  }

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % filteredAds.length)
  }

  return (
    <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          px: 3,
          py: 2,
          backgroundColor: '#f5f5f5',
          borderRadius: '4px',
          mb: 2,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Left Arrow */}
        {filteredAds.length > 1 && (
          <IconButton
            onClick={handlePrev}
            size="small"
            title={t('ad_cycling_prev')}
            aria-label={t('ad_cycling_prev')}
            sx={{ color: colors.brand }}
          >
            <ChevronLeft />
          </IconButton>
        )}

        {/* Ad Banner */}
        <ButtonBase
          onClick={handleAdClick}
          onKeyDown={handleAdKeyDown}
          tabIndex={0}
          role="button"
          aria-label={t('open_ad')}
          sx={{
            flex: 1,
            maxWidth: '1200px',
            display: 'flex',
            justifyContent: 'center',
            borderRadius: '4px',
            '&:focus-visible': {
              outline: `2px solid ${colors.brand}`,
              outlineOffset: '2px',
            },
          }}
        >
          <img
            src={adImageUrl}
            alt={altText}
            style={{
              maxWidth: '100%',
              height: 'auto',
              maxHeight: '300px',
              borderRadius: '4px',
            }}
            loading="lazy"
          />
        </ButtonBase>
        {filteredAds.length > 1 && (
          <IconButton
            onClick={handleNext}
            size="small"
            title={t('ad_cycling_next')}
            aria-label={t('ad_cycling_next')}
            sx={{ color: colors.brand }}
          >
            <ChevronRight />
          </IconButton>
        )}
      </Box>

      {/* Contact Representative Modal */}
      <Dialog
        open={showContactModal}
        onClose={() => setShowContactModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('contact_representative')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {partnerContact?.email && (
              <Box>
                <strong>{t('email')}:</strong> {partnerContact.email}
              </Box>
            )}
            {partnerContact?.phoneNumber && (
              <Box>
                <strong>{t('phone_number')}:</strong>{' '}
                {partnerContact.phoneNumber}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowContactModal(false)} color="primary">
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
